/**
 * Where a completed Central sign-in lands when it has NO real continue target.
 *
 * Owner rule (2026-10-09): a Central admin — `users.is_central_admin`, which
 * Central mints into the `centralAdmin` token claim — lands on the admin
 * console. Everyone else lands on `/home`, exactly as before.
 *
 * ONLY the no-continue landing changes. Every caller decides this AFTER its own
 * continuation handling, so an app sign-in (`/api/oidc/authorize…`), an
 * invitation, onboarding and any other validated `continue` still go where they
 * were going — admins included. `/home` itself never redirects anyone; an admin
 * who opens it directly stays there.
 *
 * HOW THE ADMIN IS RECOGNISED: by the claim on a token, the same test the
 * console guard applies (`hasCentralAdminClaims`). Nothing the sign-in flows
 * return says who is an admin, so the token comes from the settings session
 * bridge — the request an admin needs anyway, because the console reads its
 * token from `admin_token`, and that is where the bridged token is stored, as
 * settings does. An ordinary user's token is discarded, never stored.
 *
 * ONLY WHERE THE CONSOLE WORKS. `requireAdmin` 404s every `/api/admin/*` call
 * where the admin UI is off (`ADMIN_UI_ENABLED`, which defaults to off when
 * `NODE_ENV=production`), so before an admin is sent to the console, the
 * console's first read is made with their token. Only a 2xx opens it; any
 * other answer lands them on `/home` exactly as before, with nothing stored.
 * Ordinary users never reach this probe.
 *
 * NOT A SECURITY BOUNDARY. This picks a page. `requireAdmin` re-checks the
 * claim on every `/api/admin/*` call, and any failure here (no session, Central
 * down, no workspace bound, the admin API off, blocked storage) lands on
 * `/home`, never on sign-in: the user has just signed in, and bouncing them
 * back there on a blip is a loop.
 */
import { ADMIN_CONSOLE_PATH, USER_HOME_PATH, hasCentralAdminClaims } from './adminAccess';
import { requestSessionBridge, storeAdminToken } from './sessionRecovery';

/**
 * A page whose only job is the landing decision. Google sign-in returns here
 * (it is the default `returnTo`), because the browser arrives from Central's
 * own redirect with nothing else in hand.
 */
export const SIGNED_IN_LANDING_PATH = '/auth/signed-in';

export type SignInLanding = 'admin-console' | 'home';

type Navigate = (to: string, options?: { replace?: boolean }) => void;

interface LandOptions {
  /**
   * Replace the current history entry instead of pushing one. For the
   * landing page, which Back must never return to: it would only decide again.
   */
  replace?: boolean;
  fetchImpl?: typeof fetch;
}

/**
 * The admin-claimed token for the session that just signed in, or null for
 * anyone else (and for any bridge answer other than a token).
 */
export async function adminTokenForSession(
  fetchImpl: typeof fetch = fetch,
): Promise<string | null> {
  const bridge = await requestSessionBridge(fetchImpl);
  if (bridge.kind !== 'token') return null;
  return hasCentralAdminClaims(bridge.accessToken) ? bridge.accessToken : null;
}

/** The console's first read (its Overview page), behind `requireAdmin`. */
export const ADMIN_CONSOLE_PROBE_PATH = '/api/admin/system/overview';

/**
 * Does the admin API answer this token? Only a 2xx counts: 404 (admin UI off),
 * 401, 403, a 5xx and a network failure all mean the console could not load.
 */
async function adminConsoleAnswers(token: string, fetchImpl: typeof fetch): Promise<boolean> {
  try {
    const res = await fetchImpl(ADMIN_CONSOLE_PROBE_PATH, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** The landing under way, if any. */
let pending: Promise<SignInLanding> | null = null;

/**
 * Land a freshly signed-in user who has no continue target.
 *
 * Admin, once the admin API has answered their token: the token is stored as
 * `admin_token` and the console is opened with a full page load, because the
 * console's guard reads that key when the app starts. Everyone else, and an
 * admin whose console would not load: `/home`, via the router, as before.
 *
 * ONE DECISION PER SIGN-IN. A call made while another is under way gets that
 * call's answer and navigates nowhere itself. In development StrictMode runs
 * the workspace picker's effect twice, so two landings started together: the
 * console page load the first one began aborted the second one's bridge
 * request, and the second then sent the admin to `/home` on the way out.
 */
export function landAfterSignIn(navigate: Navigate, options: LandOptions = {}): Promise<SignInLanding> {
  if (!pending) {
    pending = land(navigate, options).finally(() => {
      pending = null;
    });
  }
  return pending;
}

async function land(
  navigate: Navigate,
  { replace = false, fetchImpl = fetch }: LandOptions,
): Promise<SignInLanding> {
  const adminToken = await adminTokenForSession(fetchImpl);
  // Probe BEFORE storing: a console that cannot answer leaves storage as it was.
  const consoleAnswers = adminToken !== null && (await adminConsoleAnswers(adminToken, fetchImpl));
  if (adminToken && consoleAnswers && storeAdminToken(adminToken)) {
    if (replace) window.location.replace(ADMIN_CONSOLE_PATH);
    else window.location.assign(ADMIN_CONSOLE_PATH);
    return 'admin-console';
  }
  if (replace) navigate(USER_HOME_PATH, { replace: true });
  else navigate(USER_HOME_PATH);
  return 'home';
}
